import { useCallback } from "react";
import { useRouter } from "next/router";
import { useAtomValue, useSetAtom } from "jotai";
import { appUpgradeLoadingState, freeStockTodayState, planPickerDrawerOpenState, userState } from "@/lib/store";
import { isOrtexApp } from "@/lib/utils";
import { UpgradeLocation, trackUpgradeClick } from "@/lib/tracking";
import { OFFER_HREF, OFFER_PROMO_CODE } from "@/lib/advancedOffer";
import useAdvancedOffer from "src/hooks/useAdvancedOffer";
import useAppSubscriberUpgrade from "src/hooks/useAppSubscriberUpgrade";

/**
 * The way in from a locked surface, routed by who is looking: a visitor registers (an upgrade
 * flow has no account to attach to), a subscriber in the app gets the plan picker, a subscriber
 * on the web gets the upgrade flow. Same routing as ObfuscationBanner and BondsLocked; use this
 * for new CTAs instead of repeating it.
 *
 * `label` is the button text for the viewer's plan; `extra` (and `more`, per click) go into the
 * tracked click.
 *
 * On a stock that is free today (freeStockTodayState) a visitor is asked for a free account
 * instead: signing in unlocks the data, web and app alike.
 */
const useUpgradeCta = (location: UpgradeLocation, extra: Record<string, any> = {}) => {
    const user = useAtomValue(userState);
    const loading = useAtomValue(appUpgradeLoadingState);
    const setPlanPickerOpen = useSetAtom(planPickerDrawerOpenState);
    const { handleAppSubscriberUpgrade } = useAppSubscriberUpgrade();
    const router = useRouter();
    const { push } = router;

    const plan = user?.subscription_plan;
    const isVisitor = !user || !plan || plan === "ORTEX Visitor";
    const freeStock = useAtomValue(freeStockTodayState) && isVisitor;
    // A visitor or a free account gets the first-month offer by name (the price sells better
    // than "upgrade"); a Basic subscriber upgrades at the normal price; the native app keeps
    // its store pricing.
    const offerApplies = !isOrtexApp() && (isVisitor || plan === "ORTEX Free");
    const offer = useAdvancedOffer(offerApplies);
    const label = freeStock ? "Free today: sign up" : offerApplies ? `Unlock for ${offer.offerPrice}` : plan === "ORTEX Basic" ? "Get Advanced" : "Upgrade now";
    const caption = freeStock ? "Free with an account. No card needed." : offerApplies ? `First month, then ${offer.fullPrice}/month. Cancel anytime.` : "";

    const extraKey = JSON.stringify(extra);
    const onClick = useCallback(
        (e?: { stopPropagation?: () => void }, more: Record<string, any> = {}) => {
            e?.stopPropagation?.();
            trackUpgradeClick(location, { plan, offer: offerApplies && !freeStock ? OFFER_PROMO_CODE : undefined, free_stock: freeStock, ...JSON.parse(extraKey), ...more });
            if (freeStock) push("/login?register=true");
            else if (isVisitor) push(offerApplies ? OFFER_HREF : "/login?register=true");
            else if (isOrtexApp()) setPlanPickerOpen(true);
            else if (offerApplies) {
                // The checkout opens from the URL (the ?checkout=true effect in _app), which is
                // how the promo, the monthly interval and the Advanced tier reach it.
                void router.replace({ pathname: router.pathname, query: { ...router.query, promo: OFFER_PROMO_CODE, t: "monthly", v: "advanced", checkout: "true" } }, undefined, { shallow: true });
            } else handleAppSubscriberUpgrade();
        },
        [location, plan, extraKey, isVisitor, freeStock, offerApplies, push, router, setPlanPickerOpen, handleAppSubscriberUpgrade]
    );

    return { isVisitor, plan, label, caption, loading, onClick };
};

export default useUpgradeCta;
